/*
 * Feelair velkomstpopup: question -> email -> sms (native Omnisend embedded
 * form, two steps) -> the offer. Same component as DBK's dbk-signup-popup.js.
 * Markup, the offer versions and the rules for who never sees it:
 * snippets/fa-signup-popup.liquid.
 * Visitor state lives in localStorage/sessionStorage and holds no contact data.
 * Personal code (only when the theme setting "Kodeservice" is filled in): on the first
 * quiz click the popup asks the code service for this visitor's own 7-day code, puts it
 * in two hidden Omnisend fields before the email is sent and shows/applies it at the end, but ONLY if the
 * email step's request to Omnisend really carried it (single-code gate: a customer never sees two codes).
 * Otherwise, and with a blank setting, it is the shared code from the "Rabatkode" setting, as before.
 * Add ?fa-popup=1 to any URL to clear that state and open the popup at once,
 * and &fa-arm=a|b to pick the offer version.
 */
if (!customElements.get('fa-signup-popup')) {
  const STORE_KEY = 'fa-signup-v1'; // { status: 'subscribed' | 'known' | 'dismissed', at }
  const SESSION_KEY = 'fa-signup-session'; // { firstSeen, shown, teaser }
  const ARM_KEY = 'fa-signup-arm'; // the offer version this browser keeps for the whole test
  const DAY = 86400000;
  const IMAGE_LEAD = 3000; // ms before the popup opens that its picture is requested
  const IMAGE_WAIT = 800; // ms at most that the opening waits for a picture that is still on its way
  const CODE_KEY = 'fa-welcome-code'; // { code, expires_at, expires_label } of this browser's personal code
  const VID_KEY = 'fa-welcome-vid'; // random visitor id sent to the code service (never contact data)
  const CODE_TIMEOUT = 4000; // ms per request; the service is asked twice, then the backup code is used
  const CODE_PATTERN = /^FA-[A-Z2-9]{4}-[A-Z2-9]{4}$/;
  const LABEL_MAX = 30; // characters of the expiry label
  // The two Omnisend properties that carry the personal code onto the contact. Omnisend names the form inputs, and the
  // keys of the formData it POSTs, custom_<property>.
  const FIELD_CODE = 'custom_velkomst_kode';
  const FIELD_EXPIRY = 'custom_velkomst_kode_udloeber';
  // The one Omnisend call the popup watches: every form step POSTs here (read from Omnisend's forms script, main.js).
  const SUBSCRIBE_URL = /^https:\/\/[^/]*(soundest|omnisend)[^/]*\/REST\/forms\/v2\/subscribe(\?|$)/i;
  const VID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  // Values of the hidden "popup_tilbud" radio in the Omnisend form.
  const ARM_VALUES = { a: 'A 10 procent', b: 'B Vil du have rabat' };
  const SUBMIT_TEXT = { a: 'Hent min rabat', b: 'Se min rabat' };
  const readStore = (storage, key) => {
    try { return JSON.parse(storage.getItem(key)) || {}; } catch (error) { return {}; }
  };
  const writeStore = (storage, key, value) => {
    try { storage.setItem(key, JSON.stringify(value)); } catch (error) { /* private mode: state just isn't kept */ }
  };
  const hasCookie = name => document.cookie.split('; ').some(entry => entry.startsWith(name + '='));
  // Microsoft Clarity is on the store: custom events give the step funnel (no contact data).
  const track = (...args) => { try { window.clarity?.(...args); } catch (error) { /* analytics never blocks the popup */ } };
  const readRaw = key => { try { return localStorage.getItem(key); } catch (error) { return null; } };
  const writeRaw = (key, value) => { try { localStorage.setItem(key, value); } catch (error) { /* private mode: it just isn't kept */ } };
  const readCache = () => { try { return JSON.parse(readRaw(CODE_KEY)); } catch (error) { return null; } };
  const newVid = () => {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    const bytes = new Uint8Array(16);
    if (window.crypto?.getRandomValues) window.crypto.getRandomValues(bytes);
    else bytes.forEach((_, index) => { bytes[index] = Math.floor(Math.random() * 256); });
    bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
    bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 10xx
    const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
    return [hex.slice(0, 8), hex.slice(8, 12), hex.slice(12, 16), hex.slice(16, 20), hex.slice(20)].join('-');
  };
  // The shape the code service promises: an FA code, a future expiry and a short plain-text label in Danish
  // words ("13. oktober"). The label is only ever inserted as text, but anything that looks like HTML is refused too.
  const validCode = value => Boolean(value) && typeof value === 'object'
    && typeof value.code === 'string' && CODE_PATTERN.test(value.code)
    && typeof value.expires_label === 'string' && value.expires_label.trim().length > 0 && value.expires_label.length <= LABEL_MAX && !/[<>]/.test(value.expires_label)
    && typeof value.expires_at === 'string' && Date.parse(value.expires_at) > Date.now();
  // Calendar day in Copenhagen as a whole number (date only, no time of day), for comparing cached codes by day.
  const copenhagenDay = date => {
    try {
      const parts = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Copenhagen', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(date);
      const part = type => Number(parts.find(entry => entry.type === type).value);
      return Date.UTC(part('year'), part('month') - 1, part('day')) / DAY;
    } catch (error) {
      return NaN; // no Intl time zones or an unreadable date: nothing counts as "from today", a fresh code is asked for
    }
  };
  // One try at the service, at most CODE_TIMEOUT ms. 'gone' = 410: that visitor id's code is used or has expired. Anything but a valid 200 answer counts as a failure (null).
  const requestCode = async (endpoint, vid) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), CODE_TIMEOUT);
    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        credentials: 'omit',
        cache: 'no-store',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ vid }),
        signal: controller.signal,
      });
      if (response.status === 410) return 'gone';
      if (response.status !== 200) return null;
      const data = await response.json();
      return validCode(data) ? { code: data.code, expires_at: data.expires_at, expires_label: data.expires_label } : null;
    } catch (error) {
      return null; // network error, timeout (abort) or an unreadable body
    } finally {
      clearTimeout(timer);
    }
  };
  const postJSON = (url, body) => fetch(url, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });

  customElements.define('fa-signup-popup', class extends HTMLElement {
    connectedCallback() {
      if (this.dialog) return;
      this.dialog = this.querySelector('dialog');
      this.formId = this.dataset.formId;
      // Empty = no personal codes: the popup shows and applies the shared code, as before.
      this.endpoint = /^https:\/\//i.test(this.dataset.codeEndpoint || '') ? this.dataset.codeEndpoint.trim() : '';
      this.embed = this.querySelector('[data-embed]');
      this.loading = this.querySelector('[data-loading]');
      this.loadError = this.querySelector('[data-load-error]');
      this.teaser = this.querySelector('[data-teaser]');
      this.step = 'question';
      this.desktop = window.matchMedia('(min-width: 768px)');

      const params = new URLSearchParams(window.location.search);
      const forced = params.get('fa-popup') === '1';
      if (forced) {
        try { localStorage.removeItem(STORE_KEY); sessionStorage.removeItem(SESSION_KEY); } catch (error) { /* ignore */ }
      }
      this.arm = this.pickArm(params.get('fa-arm'));
      const stored = readStore(localStorage, STORE_KEY);
      this.session = readStore(sessionStorage, SESSION_KEY);
      if (!this.session.firstSeen) {
        this.session.firstSeen = Date.now();
        this.saveSession();
      }

      if (!forced) {
        if (stored.status === 'subscribed' || stored.status === 'known') return this.remove();
        // Already on the list: arrived from one of our emails, or Omnisend knows the browser.
        if (params.get('utm_source') === 'omnisend' || hasCookie('omnisendContactID')) {
          writeStore(localStorage, STORE_KEY, { status: 'known', at: Date.now() });
          return this.remove();
        }
        // They carry a code from an ad or a landing page already; don't swap it for ours.
        if (hasCookie('discount_code')) return this.remove();
        const declinedRecently = stored.status === 'dismissed' && Date.now() - stored.at < Number(this.dataset.reshowDays) * DAY;
        if (declinedRecently || this.session.shown) {
          if (!this.session.teaser) return this.remove();
          this.listen();
          this.showTeaser();
          return;
        }
      }

      this.listen();
      const delay = forced ? 300 : Math.max(this.session.firstSeen + Number(this.dataset.delay) * 1000 - Date.now(), 1500);
      this.preloadTimer = setTimeout(() => this.loadImages(), Math.max(delay - IMAGE_LEAD, 0));
      this.openTimer = setTimeout(() => this.tryOpen(), delay);
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.embedObserver?.disconnect();
      clearTimeout(this.preloadTimer);
      clearTimeout(this.openTimer);
      clearTimeout(this.loadingTimeout);
    }

    // One version per browser for the whole test, chosen at random the first time.
    pickArm(requested) {
      const available = ['a', 'b'];
      if (available.includes(requested)) return requested;
      const mode = this.dataset.offerMode;
      if (mode !== 'test') return available.includes(mode) ? mode : 'a';
      let arm = (readStore(localStorage, ARM_KEY) || {}).arm;
      if (!available.includes(arm)) {
        const random = new Uint32Array(1);
        window.crypto.getRandomValues(random);
        arm = available[random[0] % available.length];
        writeStore(localStorage, ARM_KEY, { arm, at: Date.now() });
      }
      return arm;
    }

    listen() {
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.dialog.dataset.arm = this.arm;
      this.querySelectorAll('[data-for-arm]').forEach(element => {
        element.hidden = !element.dataset.forArm.split(' ').includes(this.arm);
      });
      track('set', 'fa_popup_arm', this.arm);
      this.querySelectorAll('[data-answer]').forEach(button => {
        button.addEventListener('click', () => this.answer(button.dataset.answer), options);
      });
      this.querySelector('[data-decline]').addEventListener('click', () => {
        track('event', 'fa_popup_declined_' + this.step);
        this.dialog.close();
      }, options);
      this.querySelector('[data-skip-sms]').addEventListener('click', () => {
        track('event', 'fa_popup_sms_skipped');
        this.finish();
      }, options);
      this.querySelector('[data-close]').addEventListener('click', () => this.dialog.close(), options);
      this.querySelector('[data-copy]').addEventListener('click', () => this.copyCode(), options);
      this.querySelector('[data-retry]').addEventListener('click', () => window.location.reload(), options);
      this.querySelector('[data-teaser-open]').addEventListener('click', () => this.whenImagesReady(IMAGE_WAIT).then(() => this.open()), options);
      this.querySelector('[data-teaser-close]').addEventListener('click', () => {
        this.session.teaser = false;
        this.saveSession();
        this.teaser.hidden = true;
      }, options);
      // Escape is the keyboard way out: a "no thanks" before the signup, "kun mail" on the sms step.
      this.dialog.addEventListener('cancel', event => {
        if (this.step === 'sms') {
          event.preventDefault();
          track('event', 'fa_popup_sms_skipped');
          this.finish();
        } else if (this.step !== 'done') {
          track('event', 'fa_popup_declined_' + this.step);
        }
      }, options);
      this.dialog.addEventListener('close', () => this.onClose(), options);

      this.watchSubscribe();
      // The personal code has to be in the form before the email is sent: capture the submit on the way down.
      this.embed.addEventListener('submit', event => this.holdSubmit(event), { ...options, capture: true });
      this.embedObserver = new MutationObserver(() => this.checkForm());
      this.embedObserver.observe(this.embed, { childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'class', 'hidden'] });
      window.addEventListener('omnisendForms', event => {
        const detail = event.detail || {};
        if (detail.form?.id !== this.formId) return;
        // Omnisend fires "submit" (and "stepSubmit") only after its API accepted
        // the data. Never read detail.formValues: the theme does not touch contact data.
        if (detail.type === 'submit' || (detail.type === 'stepSubmit' && String(detail.step) === '1')) this.emailDone();
        else if (detail.type === 'stepSubmit') {
          if (this.step === 'sms') track('event', 'fa_popup_sms_submitted');
          this.finish();
        }
        else this.checkForm();
      }, options);
      this.checkForm();
    }

    blocked() {
      // The cookie choice comes first: never cover Shopify's consent banner.
      if (document.getElementById('shopify-pc__banner')?.getClientRects().length) return true;
      // The Feelair theme marks an open drawer on <html> and an open modal with .modal--is-active.
      if (document.documentElement.classList.contains('js-drawer-open')) return true;
      if (document.querySelector('.modal--is-active, dialog[open]')) return true;
      const active = document.activeElement;
      return Boolean(active && active.matches('input, textarea, select, [contenteditable="true"]'));
    }

    tryOpen() {
      if (document.hidden || this.blocked()) {
        this.openTimer = setTimeout(() => this.tryOpen(), 1000);
        return;
      }
      // The photo is on its way (it starts IMAGE_LEAD ms before this moment): wait for it, but only once and never longer than
      // IMAGE_WAIT ms, so the dialog does not open with an empty photo panel. Then the same checks run again.
      if (!this.waitedForImages && this.pendingImages().length) {
        this.waitedForImages = true;
        this.whenImagesReady(IMAGE_WAIT).then(() => this.tryOpen());
        return;
      }
      this.open();
    }

    // The photo is desktop only, the logo band phones only: each screen downloads just its own picture.
    forThisScreen(image) {
      if (image.closest('.fa-signup__photo')) return this.desktop.matches;
      if (image.closest('.fa-signup__brand')) return !this.desktop.matches;
      return true;
    }

    loadImages() {
      this.querySelectorAll('img[data-src]').forEach(image => {
        if (!this.forThisScreen(image)) return;
        image.src = image.dataset.src;
        image.removeAttribute('data-src');
      });
    }

    // Images this screen shows that are requested but not in yet (a broken image counts as in: it never will be).
    pendingImages() {
      return Array.from(this.querySelectorAll('img[src]')).filter(image => !image.complete && this.forThisScreen(image));
    }

    // Starts the images if need be and resolves when they are in, or after maxMs, whichever comes first.
    whenImagesReady(maxMs) {
      this.loadImages();
      const pending = this.pendingImages();
      if (!pending.length) return Promise.resolve();
      const arrived = Promise.all(pending.map(image => new Promise(resolve => {
        image.addEventListener('load', resolve, { once: true });
        image.addEventListener('error', resolve, { once: true });
      })));
      return Promise.race([arrived, new Promise(resolve => setTimeout(resolve, maxMs))]);
    }

    open() {
      if (this.dialog.open) return;
      this.loadImages();
      this.lastFocus = document.activeElement;
      this.teaser.hidden = true;
      this.session.shown = true;
      this.saveSession();
      this.dialog.showModal();
      track('event', 'fa_popup_shown');
      track('event', 'fa_popup_shown_' + this.arm);
      this.go(this.step);
    }

    go(step) {
      this.step = step;
      this.dialog.dataset.step = step;
      this.querySelectorAll('[data-step-panel]').forEach(panel => {
        panel.hidden = !panel.dataset.stepPanel.split(' ').includes(step);
      });
      this.querySelectorAll('[data-for-step]').forEach(element => { element.hidden = element.dataset.forStep !== step; });
      // B reveals the amount only after the signup.
      const offer = this.thanks ? 'thanks' : (this.arm === 'b' && step === 'done' ? 'b-done' : this.arm);
      this.querySelectorAll('[data-offer]').forEach(element => { element.hidden = element.dataset.offer !== offer; });
      this.querySelector('[data-code-block]').hidden = Boolean(this.thanks);
      this.querySelector('[data-step-footer]').hidden = step !== 'email'; // the sms step carries Omnisend's own consent text
      this.querySelector('[data-decline]').hidden = step !== 'question' && step !== 'email';
      this.querySelector('[data-skip-sms]').hidden = step !== 'sms';
      this.dialog.scrollTop = 0;
      if (step === 'email') {
        track('event', 'fa_popup_email_step');
        this.checkForm();
        const input = this.embed.querySelector('input[type="email"]');
        if (input) {
          input.focus();
        } else {
          this.loading.hidden = false;
          clearTimeout(this.loadingTimeout);
          this.loadingTimeout = setTimeout(() => {
            if (this.step === 'email' && !this.embed.querySelector('input[type="email"]')) {
              this.loading.hidden = true;
              this.loadError.hidden = false;
            }
          }, 12000);
        }
      } else if (step === 'sms') {
        track('event', 'fa_popup_sms_step');
        this.checkForm();
        this.embed.querySelector('input[type="tel"]')?.focus();
      } else if (step === 'done') {
        this.querySelector(this.thanks ? '[data-close]' : '[data-copy]').focus({ preventScroll: true });
      } else {
        this.querySelector('#FaSignupOffer').focus({ preventScroll: true });
      }
    }

    answer(value) {
      this.answerValue = value;
      this.startCode();
      this.selectRadios();
      track('set', 'fa_popup_answer', value);
      track('event', 'fa_popup_answered');
      this.go('email');
    }

    // The quiz answer and the offer version are radio fields in the Omnisend
    // form, hidden by the theme and ticked here, so both land on the contact
    // as properties (hjaelp_til, popup_tilbud).
    selectRadios() {
      const radios = Array.from(this.embed.querySelectorAll('input[type="radio"]'));
      [this.answerValue, ARM_VALUES[this.arm]].filter(Boolean).forEach(value => {
        const wanted = value.trim().toLowerCase();
        const radio = radios.find(input => {
          const label = input.closest('label') || (input.id && this.embed.querySelector('label[for="' + input.id + '"]'));
          return input.value.trim().toLowerCase() === wanted || (label?.textContent || '').trim().toLowerCase() === wanted;
        });
        if (radio && !radio.checked) radio.click();
      });
    }

    // The personal code. Started on the first quiz click, so it is ready long before the email is typed.
    // The promise resolves to { code, expires_at, expires_label }, or null = backup mode (the shared
    // code from the "Rabatkode" setting). It never rejects.
    startCode() {
      if (!this.endpoint) return Promise.resolve(null);
      if (!this.codePromise) {
        this.codePending = true;
        this.codePromise = this.loadCode().then(personal => {
          this.personal = personal;
          this.codePending = false;
          track('event', personal ? 'fa_popup_code_personal' : 'fa_popup_code_backup');
          this.fillCodeFields();
          return personal;
        });
      }
      return this.codePromise;
    }

    async loadCode() {
      try {
        // The service ends a code 7 days after the day it was made (Copenhagen time), so a cached code is only
        // good while the Copenhagen date of its expires_at is today + 7 (dates, not times). A code from an
        // earlier day, an expired or an unreadable cache starts over with a NEW visitor id.
        const hadCache = readRaw(CODE_KEY) !== null;
        const cached = readCache();
        if (validCode(cached) && copenhagenDay(new Date(cached.expires_at)) >= copenhagenDay(new Date()) + 7) {
          return { code: cached.code, expires_at: cached.expires_at, expires_label: cached.expires_label };
        }
        let vid = readRaw(VID_KEY);
        if (!VID_PATTERN.test(vid || '') || hadCache) {
          vid = newVid();
          writeRaw(VID_KEY, vid);
        }
        let result = await requestCode(this.endpoint, vid);
        if (!result || result === 'gone') {
          if (result === 'gone') {
            vid = newVid(); // that vid's code is used or expired: drop it. The new vid makes this the one retry.
            writeRaw(VID_KEY, vid);
          }
          result = await requestCode(this.endpoint, vid);
        }
        if (result === 'gone') {
          try { localStorage.removeItem(VID_KEY); } catch (error) { /* private mode */ }
          result = null;
        }
        if (result) writeRaw(CODE_KEY, JSON.stringify(result));
        return result;
      } catch (error) {
        return null; // whatever went wrong, the popup falls back to the backup code
      }
    }

    // The two hidden text fields of the Omnisend form copy that carry the personal code onto the contact
    // (properties velkomst_kode and velkomst_kode_udloeber). Same technique as selectRadios: found by the
    // field's name, else by its label, aria-label or placeholder text. The live form has neither field, so
    // nothing is found and nothing happens.
    // selector: the exact Omnisend DOM name (read from the form copy 6ac51b8cfbdffc9814adf1bf); swap it here if the field is ever recreated
    findCodeField() { return this.findTextField(FIELD_CODE, ['velkomst_kode', 'velkomstkode', 'kode']); }

    // selector: the exact Omnisend DOM name (read from the form copy 6ac51b8cfbdffc9814adf1bf); swap it here if the field is ever recreated
    findExpiryField() { return this.findTextField(FIELD_EXPIRY, ['velkomst_kode_udloeber', 'kode udløber', 'dato']); }

    findTextField(name, texts) {
      const inputs = Array.from(this.embed.querySelectorAll('input[type="text"], input:not([type])'));
      const same = (a, b) => (a || '').trim().toLowerCase() === b;
      return inputs.find(input => same(input.name, name))
        || inputs.find(input => {
          const label = input.closest('label') || (input.id && this.embed.querySelector('label[for="' + CSS.escape(input.id) + '"]'));
          return [label?.textContent, input.getAttribute('aria-label'), input.placeholder].some(text => texts.some(wanted => same(text, wanted)));
        }) || null;
    }

    // Hides both fields like the radios (always: with the form copy selected and the setting blank they must
    // never show) and fills them with the personal code, BOTH or NONE: one input alone never gets a code, so a
    // personal code can only leave in the form together with its expiry. In backup mode, with only one input in
    // the form, or with the setting blank, they stay empty (and anything in them is emptied).
    fillCodeFields() {
      const code = this.findCodeField();
      const expiry = this.findExpiryField();
      const both = Boolean(this.endpoint && this.personal && code && expiry);
      [[code, both && this.personal.code], [expiry, both && this.personal.expires_label]].forEach(([input, value]) => {
        if (!input) return;
        this.embed.querySelectorAll('div[class*="-field-container-"]').forEach(box => {
          if (box.contains(input) && !box.hidden) box.hidden = true;
        });
        // Once the email request has gone out the fields are left alone (checkFields lifts this for the next attempt).
        if (this.emailSent) return;
        // The native setter plus both events, so Omnisend gets the value whether it reads the DOM or listens to input.
        if (input.value !== (value || '')) {
          Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value').set.call(input, value || '');
          input.dispatchEvent(new Event('input', { bubbles: true }));
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }

    // Single-code gate, part 1 (the DOM, on the way into the email submit, before Omnisend reads the form): both inputs
    // are there and hold exactly the personal code and its label, or both are empty. Whatever the popup will show, the
    // contact must not get a code that differs from it.
    checkFields() {
      this.emailSent = false; // a new attempt: verify (and if need be fill or empty) the fields again
      this.fillCodeFields();
      const code = this.findCodeField();
      const expiry = this.findExpiryField();
      this.fieldsOk = Boolean(this.personal && code && expiry && code.value === this.personal.code && expiry.value === this.personal.expires_label);
    }

    // Single-code gate, part 2 (what really left): the personal code is shown only if the service gave one AND the email
    // step's request to Omnisend carried both properties with exactly its code and label. If that request could not be read
    // (sentFields unset), the DOM check at submit decides. Every other case shows the backup code and sent nothing.
    singleCode(personal) {
      if (!personal) return false;
      if (this.sentFields) return this.sentFields.code === personal.code && this.sentFields.label === personal.expires_label;
      return this.fieldsOk === true;
    }

    // If the code request has not finished when the email is submitted, hold the submit until it has
    // (at most two tries of 4 s), fill the two fields, then send it again exactly as Omnisend would have.
    holdSubmit(event) {
      if (!this.endpoint) return;
      const form = event.target;
      if (!form.querySelector?.('input[type="email"]')) return; // the code fields live on the email step only
      if (!this.codePromise || !this.codePending) return this.checkFields();
      event.preventDefault();
      event.stopImmediatePropagation();
      if (this.holding) return;
      this.holding = true;
      const submitter = event.submitter;
      form.setAttribute('aria-busy', 'true');
      this.codePromise.then(() => {
        this.holding = false;
        form.removeAttribute('aria-busy');
        // Same submit as the visitor's own, with the same button (the check runs when it comes back through here).
        // requestSubmit is missing before Safari 16: click instead.
        if (typeof form.requestSubmit === 'function') form.requestSubmit(submitter || undefined);
        else (submitter || form.querySelector('button[type="submit"]'))?.click();
      });
    }

    // Already-subscribed visitors, step 1 (always on, with or without the code service). Omnisend's form script POSTs every step to
    // /REST/forms/v2/subscribe and decides its own "already subscribed" section from ONE field of the JSON answer:
    // formState, exactly the string "subscribed" (anything else, or nothing, shows its success section). Omnisend only
    // reads it after the last step, so the popup reads the same field from the answer to the EMAIL step.
    // This is a pass-through wrapper scoped to that one URL: the request goes out untouched, a rejection reaches the
    // caller unchanged, and the caller gets the very same Response object. The only difference: the answer is handed on
    // after the popup has read a clone of it, so the thank-you is up before Omnisend moves its own form to the phone step.
    // Any problem in here is swallowed and the flow simply goes on as before.
    watchSubscribe() {
      if (this.watching || typeof window.fetch !== 'function') return;
      this.watching = true;
      const nativeFetch = window.fetch;
      const popup = this;
      window.fetch = function (input) {
        const result = nativeFetch.apply(this, arguments);
        try {
          const url = typeof input === 'string' ? input : ((input && input.url) || '');
          if (!SUBSCRIBE_URL.test(url)) return result;
          const stepAtCall = popup.step; // 'email' for the email step, 'sms' for the phone step
          if (stepAtCall === 'email' && popup.endpoint) popup.readSentFields(arguments[1]); // the single-code gate: personal-code mode only
          return result.then(async response => {
            try { popup.subscribeAnswered(stepAtCall, await response.clone().json()); } catch (error) { /* not JSON: nothing to see */ }
            return response;
          });
        } catch (error) { /* watching never breaks the form */ }
        return result;
      };
    }

    // What the email step's request carries in the two code properties. ONLY those two keys are read from the body
    // (nothing else in it is looked at or kept). Unreadable body: sentFields stays unset and the DOM check decides.
    readSentFields(init) {
      this.emailSent = true;
      this.sentFields = undefined;
      try {
        const formData = JSON.parse(init.body).formData;
        if (formData && typeof formData === 'object') this.sentFields = { code: formData[FIELD_CODE], label: formData[FIELD_EXPIRY] };
      } catch (error) { /* not a JSON string: sentFields stays unset */ }
    }

    subscribeAnswered(stepAtCall, data) {
      if (stepAtCall !== 'email' || !data || typeof data !== 'object' || data.error) return;
      // What Omnisend sent at step 1, as a tag (a short word such as "success"), so the live behaviour can be checked in Clarity.
      track('set', 'fa_popup_step1_state', String(data.formState).replace(/[^a-z]/gi, '').slice(0, 20));
      if (data.formState === 'subscribed') this.knownSubscriber();
    }

    // The email is already on the list: a thank-you right away. No code, nothing applied, no phone step. In personal-code
    // mode the step-1 request with the two hidden code fields has already gone (Omnisend overwrites those two properties;
    // existing subscribers never re-enter the Welcome flow), and nothing else is sent for this contact: the phone step never shows.
    knownSubscriber() {
      if (this.step === 'done') return;
      writeStore(localStorage, STORE_KEY, { status: 'subscribed', at: Date.now() });
      this.session.teaser = false;
      this.saveSession();
      this.showThanks();
    }

    showThanks() {
      track('event', 'fa_popup_already_subscribed');
      this.thanks = true;
      this.go('done');
    }

    // Fallback, after the last step: Omnisend shows its "already subscribed" section right after it fires its events, not before.
    omnisendSubscribed() {
      return Array.from(this.embed.querySelectorAll('[class$="-subscribed-sections-container"]'))
        .some(element => element.getClientRects().length > 0);
    }

    checkForm() {
      const email = this.embed.querySelector('input[type="email"]');
      if (!email) return;
      if (!email.dataset.faStyled) {
        email.dataset.faStyled = 'true';
        email.setAttribute('aria-label', 'Din mailadresse');
        email.setAttribute('inputmode', 'email');
        email.setAttribute('autocomplete', 'email');
        email.setAttribute('autocapitalize', 'none');
        email.placeholder = 'dig@eksempel.dk';
        this.addLabel(email, 'Din mailadresse');
        const submit = email.closest('form')?.querySelector('button[type="submit"]');
        if (submit && SUBMIT_TEXT[this.arm]) submit.textContent = SUBMIT_TEXT[this.arm];
      }
      const phone = this.embed.querySelector('input[type="tel"]');
      if (phone && !phone.dataset.faStyled) {
        phone.dataset.faStyled = 'true';
        phone.setAttribute('aria-label', 'Dit mobilnummer');
        phone.setAttribute('autocomplete', 'tel-national');
        this.addLabel(phone, 'Dit mobilnummer');
      }
      this.selectRadios();
      this.fillCodeFields();
      this.loading.hidden = true;
      this.loadError.hidden = true;
      clearTimeout(this.loadingTimeout);
      if ((this.step !== 'email' && this.step !== 'sms') || !this.dialog.open) return;
      // Backup for the events: Omnisend's own success/already-subscribed state became visible.
      const confirmed = Array.from(this.embed.querySelectorAll('[class$="-success-sections-container"], [class$="-subscribed-sections-container"]'))
        .some(element => element.getClientRects().length > 0);
      if (confirmed) this.finish();
    }

    addLabel(input, text) {
      if (this.embed.querySelector('label[for="' + input.id + '"]')) return;
      const label = document.createElement('label');
      label.className = 'fa-signup__label';
      label.htmlFor = input.id;
      label.textContent = text;
      // A phone input sits in a flex row with the country prefix: the label goes above the whole row.
      (input.closest('[class$="-field-wrapper"]') || input).before(label);
    }

    // The email is on the list. Count it as a signup now, then ask for the phone number.
    emailDone() {
      if (this.step !== 'email') return;
      writeStore(localStorage, STORE_KEY, { status: 'subscribed', at: Date.now() });
      this.session.teaser = false;
      this.saveSession();
      track('event', 'fa_popup_subscribed');
      track('event', 'fa_popup_subscribed_' + this.arm);
      if (this.embed.querySelector('input[type="tel"]')) this.go('sms');
      else this.finish();
    }

    finish() {
      if (this.step === 'done' || this.finishing) return;
      writeStore(localStorage, STORE_KEY, { status: 'subscribed', at: Date.now() });
      this.session.teaser = false;
      this.saveSession();
      // Wait for the code request if it is still running (personal-code mode), and one tick more so Omnisend has
      // painted its own "already subscribed" section (it comes after its events).
      this.finishing = true;
      (this.endpoint ? this.startCode() : Promise.resolve(null)).then(personal => setTimeout(() => {
        this.finishing = false;
        if (this.step === 'done') return; // the email step's answer already said "subscribed"
        if (this.omnisendSubscribed()) return this.showThanks();
        if (this.endpoint && this.singleCode(personal)) return this.showDone(personal.code, personal.expires_label);
        // Backup mode (or no code service): the shared code, no expiry line. A service code that did NOT go out in the form
        // (a form without the inputs, values not sent) ends here too: the contact then has no personal code, so only this one exists.
        if (personal) track('event', 'fa_popup_code_gate_backup');
        this.showDone(this.dataset.code, '');
      }, 0));
    }

    // The end of the flow: the code (with its expiry line when it is a personal one), applied to the cart.
    showDone(code, expiresLabel) {
      this.activeCode = code;
      this.querySelector('[data-code-value]').textContent = code;
      this.querySelector('.fa-signup__code').toggleAttribute('data-long', code.length > 8); // a personal code is longer than AIR10: CSS gives it room
      const expiry = this.querySelector('[data-expiry]');
      expiry.hidden = !expiresLabel;
      if (expiresLabel) expiry.textContent = 'Gælder til ' + expiresLabel + ' kl. 23.59'; // the label as the service wrote it, as text
      this.go('done');
      this.applyCode(code).then(applied => {
        if (applied) this.querySelector('[data-applied]').textContent = 'Koden ligger allerede i din kurv.';
      });
    }

    // /discount/<code> stores the code in the discount_code cookie, which checkout
    // applies; /cart/update.js puts it on the cart so the drawer shows it too.
    async applyCode(code) {
      if (!code) return false;
      try {
        await fetch('/discount/' + encodeURIComponent(code) + '?redirect=%2Fcart.js', { credentials: 'same-origin' });
        await postJSON('/cart/update.js', { discount: code });
      } catch (error) {
        return false; // the visible code still works at checkout
      }
      return hasCookie('discount_code');
    }


    copyCode() {
      const button = this.querySelector('[data-copy]');
      const code = this.activeCode || this.dataset.code;
      const done = () => {
        button.textContent = 'Kopieret';
        setTimeout(() => { button.textContent = 'Kopiér'; }, 2000);
      };
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(code).then(done).catch(() => this.selectCode());
      } else {
        this.selectCode();
      }
    }

    selectCode() {
      const range = document.createRange();
      range.selectNodeContents(this.querySelector('[data-code-value]'));
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
    }

    onClose() {
      clearTimeout(this.loadingTimeout);
      // Past the email step they are signed up; closing never counts as a "no thanks".
      if ((this.step === 'question' || this.step === 'email') && !this.finishing) {
        writeStore(localStorage, STORE_KEY, { status: 'dismissed', at: Date.now() });
        this.session.teaser = true;
        this.saveSession();
        this.showTeaser();
      }
      if (this.lastFocus?.isConnected) this.lastFocus.focus({ preventScroll: true });
    }

    showTeaser() {
      // Phones too since 2026-10-06 (Kasper): the visitor keeps a way back to the offer; CSS makes it compact there.
      this.teaser.hidden = !this.session.teaser;
    }

    saveSession() {
      writeStore(sessionStorage, SESSION_KEY, this.session);
    }
  });
}
